/**
 * Export shapes for every report, kept beside the panels but in their own
 * module so each panel file exports only its component (the react-refresh
 * boundary), and so the CSV/XLSX/print paths share one definition of what a
 * report's tables are.
 */
import { centsColumn, type ExportTable } from "./reportExport";

/** Shared label/derivation helpers used by the export builders. */
const pretty = (value: string) =>
  value.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

const daysSince = (iso: string) =>
  Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);

export interface ExecutiveReport {
  revenue_collected_cents: number;
  invoiced_cents: number;
  outstanding_cents: number;
  new_contacts: number;
  deals_won: number;
  deals_won_cents: number;
  open_pipeline_cents: number;
  jobs_completed: number;
  jobs_open: number;
  revenue_by_month: { month: string; cents: number }[];
}

export const executiveTables = (data: ExecutiveReport): ExportTable[] => [
  {
    name: "Summary",
    columns: ["Metric", "Value"],
    rows: [
      ["Revenue collected", centsColumn(data.revenue_collected_cents)],
      ["Invoiced", centsColumn(data.invoiced_cents)],
      ["Outstanding (current)", centsColumn(data.outstanding_cents)],
      ["Open pipeline (current)", centsColumn(data.open_pipeline_cents)],
      ["New contacts", data.new_contacts],
      ["Deals won", data.deals_won],
      ["Deals won value", centsColumn(data.deals_won_cents)],
      ["Jobs completed", data.jobs_completed],
      ["Jobs open (current)", data.jobs_open],
    ],
  },
  {
    name: "Revenue by month",
    columns: ["Month", "Collected"],
    rows: data.revenue_by_month.map((row) => [
      row.month,
      centsColumn(row.cents),
    ]),
  },
];

export interface SalesReport {
  deals_created: number;
  deals_won: number;
  deals_lost: number;
  win_rate_bp: number | null;
  won_cents: number;
  avg_deal_cents: number | null;
  open_pipeline_cents: number;
  weighted_pipeline_cents: number;
  by_stage: { stage: string; label: string; count: number; cents: number }[];
  by_owner: {
    name: string;
    won: number;
    lost: number;
    won_cents: number;
    open_cents: number;
  }[];
  lost_reasons: { reason: string; count: number }[];
  stale_deals: {
    id: number;
    name: string;
    stage: string;
    amount: number | null;
    updated_at: string;
  }[];
}

export const salesTables = (data: SalesReport): ExportTable[] => [
  {
    name: "Summary",
    columns: ["Metric", "Value"],
    rows: [
      ["Deals created", data.deals_created],
      ["Deals won", data.deals_won],
      ["Deals lost", data.deals_lost],
      ["Win rate %", data.win_rate_bp == null ? "" : data.win_rate_bp / 100],
      ["Won value", centsColumn(data.won_cents)],
      ["Average won deal", centsColumn(data.avg_deal_cents)],
      ["Open pipeline", centsColumn(data.open_pipeline_cents)],
      ["Weighted pipeline", centsColumn(data.weighted_pipeline_cents)],
    ],
  },
  {
    name: "Pipeline by stage",
    columns: ["Stage", "Deals", "Value"],
    rows: data.by_stage.map((row) => [
      row.label,
      row.count,
      centsColumn(row.cents),
    ]),
  },
  {
    name: "By owner",
    columns: ["Owner", "Won", "Lost", "Won value", "Open value"],
    rows: data.by_owner.map((row) => [
      row.name,
      row.won,
      row.lost,
      centsColumn(row.won_cents),
      centsColumn(row.open_cents),
    ]),
  },
  {
    name: "Lost reasons",
    columns: ["Reason", "Deals"],
    rows: data.lost_reasons.map((row) => [row.reason, row.count]),
  },
  {
    name: "Stale deals",
    columns: ["Deal", "Stage", "Value", "Days untouched"],
    rows: data.stale_deals.map((row) => [
      row.name,
      row.stage,
      centsColumn(row.amount),
      daysSince(row.updated_at),
    ]),
  },
];

export interface MarketingReport {
  new_contacts: number;
  form_submissions: number;
  campaigns_sent: number;
  by_source: { source: string; contacts: number; customers: number }[];
  by_lifecycle: { stage: string; count: number }[];
  by_form: { name: string; submissions: number; deals: number }[];
  campaigns: {
    name: string;
    channel: string;
    status: string;
    sent_at: string | null;
    recipients: number;
    delivered: number;
    suppressed: number;
    failed: number;
  }[];
}

export const marketingTables = (data: MarketingReport): ExportTable[] => [
  {
    name: "Summary",
    columns: ["Metric", "Value"],
    rows: [
      ["New contacts", data.new_contacts],
      ["Form submissions", data.form_submissions],
      ["Campaigns sent", data.campaigns_sent],
    ],
  },
  {
    name: "Leads by source",
    columns: ["Source", "Contacts", "Became customers", "Conversion %"],
    rows: data.by_source.map((row) => [
      row.source,
      row.contacts,
      row.customers,
      row.contacts === 0
        ? ""
        : Math.round((row.customers / row.contacts) * 100),
    ]),
  },
  {
    name: "Contacts by lifecycle stage",
    columns: ["Stage", "Contacts"],
    rows: data.by_lifecycle.map((row) => [pretty(row.stage), row.count]),
  },
  {
    name: "Forms",
    columns: ["Form", "Submissions", "Deals created"],
    rows: data.by_form.map((row) => [row.name, row.submissions, row.deals]),
  },
  {
    name: "Campaigns",
    columns: [
      "Campaign",
      "Channel",
      "Status",
      "Sent at",
      "Recipients",
      "Delivered",
      "Suppressed",
      "Failed",
    ],
    rows: data.campaigns.map((row) => [
      row.name,
      row.channel,
      row.status,
      row.sent_at ?? "",
      row.recipients,
      row.delivered,
      row.suppressed,
      row.failed,
    ]),
  },
];

export interface TasksReport {
  open: number;
  overdue: number;
  due_next_7_days: number;
  completed: number;
  by_priority: { priority: string; count: number; overdue: number }[];
  by_owner: {
    name: string;
    open: number;
    overdue: number;
    completed: number;
  }[];
  jobs_by_status: { status: string; count: number }[];
}

export const tasksTables = (data: TasksReport): ExportTable[] => [
  {
    name: "Summary",
    columns: ["Metric", "Value"],
    rows: [
      ["Open", data.open],
      ["Overdue", data.overdue],
      ["Due next 7 days", data.due_next_7_days],
      ["Completed in period", data.completed],
    ],
  },
  {
    name: "Open tasks by priority",
    columns: ["Priority", "Open", "Overdue"],
    rows: data.by_priority.map((row) => [
      pretty(row.priority),
      row.count,
      row.overdue,
    ]),
  },
  {
    name: "By owner",
    columns: ["Owner", "Open", "Overdue", "Completed in period"],
    rows: data.by_owner.map((row) => [
      row.name,
      row.open,
      row.overdue,
      row.completed,
    ]),
  },
  {
    name: "Jobs by status",
    columns: ["Status", "Jobs"],
    rows: data.jobs_by_status.map((row) => [pretty(row.status), row.count]),
  },
];

export interface FinanceReport {
  invoiced_cents: number;
  collected_cents: number;
  refunded_cents: number;
  written_off_cents: number;
  outstanding_cents: number;
  aging: {
    current: number;
    d1_30: number;
    d31_60: number;
    d61_90: number;
    d90_plus: number;
  };
  by_status: { status: string; count: number; cents: number }[];
  by_method: { method: string; count: number; cents: number }[];
  estimates: {
    sent: number;
    accepted: number;
    declined: number;
    accepted_cents: number;
  };
  top_customers: { name: string; cents: number }[];
}

/** AR aging buckets, in order. Shared by the panel and the export. */
export const AGING_LABELS: {
  key: keyof FinanceReport["aging"];
  label: string;
}[] = [
  { key: "current", label: "Current" },
  { key: "d1_30", label: "1 to 30 days late" },
  { key: "d31_60", label: "31 to 60 days late" },
  { key: "d61_90", label: "61 to 90 days late" },
  { key: "d90_plus", label: "90+ days late" },
];

export const financeTables = (data: FinanceReport): ExportTable[] => [
  {
    name: "Summary",
    columns: ["Metric", "Value"],
    rows: [
      ["Invoiced", centsColumn(data.invoiced_cents)],
      ["Collected", centsColumn(data.collected_cents)],
      ["Refunded", centsColumn(data.refunded_cents)],
      ["Written off", centsColumn(data.written_off_cents)],
      ["Outstanding (current)", centsColumn(data.outstanding_cents)],
    ],
  },
  {
    name: "Accounts receivable aging",
    columns: ["Bucket", "Balance"],
    rows: AGING_LABELS.map((bucket) => [
      bucket.label,
      centsColumn(data.aging[bucket.key]),
    ]),
  },
  {
    name: "Invoices by status",
    columns: ["Status", "Invoices", "Value"],
    rows: data.by_status.map((row) => [
      pretty(row.status),
      row.count,
      centsColumn(row.cents),
    ]),
  },
  {
    name: "Payments by method",
    columns: ["Method", "Payments", "Value"],
    rows: data.by_method.map((row) => [
      pretty(row.method),
      row.count,
      centsColumn(row.cents),
    ]),
  },
  {
    name: "Estimates",
    columns: ["Metric", "Value"],
    rows: [
      ["Sent", data.estimates.sent],
      ["Accepted", data.estimates.accepted],
      ["Declined", data.estimates.declined],
      ["Accepted value", centsColumn(data.estimates.accepted_cents)],
    ],
  },
  {
    name: "Top customers by payments",
    columns: ["Customer", "Paid"],
    rows: data.top_customers.map((row) => [row.name, centsColumn(row.cents)]),
  },
];

export interface GeographyReport {
  by_city: {
    city: string;
    state: string;
    contacts: number;
    jobs: number;
    revenue_cents: number;
  }[];
  jobs_by_city: { city: string; jobs: number; completed: number }[];
}

export const geographyTables = (data: GeographyReport): ExportTable[] => [
  {
    name: "Revenue by customer city",
    columns: ["City", "State", "Contacts", "Jobs", "Revenue"],
    rows: data.by_city.map((row) => [
      row.city,
      row.state,
      row.contacts,
      row.jobs,
      centsColumn(row.revenue_cents),
    ]),
  },
  {
    name: "Jobs by site city",
    columns: ["City", "Jobs", "Completed"],
    rows: data.jobs_by_city.map((row) => [row.city, row.jobs, row.completed]),
  },
];

export interface ForecastMonth {
  month_start: string;
  label: string;
  booked_cents: number;
  weighted_cents: number;
  best_case_cents: number;
}

export interface ForecastReport {
  months: ForecastMonth[];
  undated_weighted_cents: number;
  undated_deals: number;
}

export const forecastTables = (data: ForecastReport): ExportTable[] => [
  {
    name: "Forecast",
    columns: ["Month", "Booked", "Weighted pipeline", "Best case"],
    rows: data.months.map((month) => [
      month.label,
      month.booked_cents / 100,
      month.weighted_cents / 100,
      month.best_case_cents / 100,
    ]),
  },
];
